import React, { useContext, useEffect, Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { isMobileOnly } from 'react-device-detect'
import moment from 'moment'

import { SEARCH, SearchContext } from '../search/SearchContext'
import SearchFilter from '../search/SearchFilter'
import MapView from '../map/MapView'
import HotelItem from './HotelItem'
import HotelInfo from './HotelInfo'
import HotelRooms from './HotelRooms'
import HotelBooking from './HotelBooking'
import HotelsPagination from './HotelsPagination'

import { Button } from '../common/forms'
import {
  isTourOnlyUser,
  shouldRenderBookNow,
  shouldRenderBookRoomsAndTour,
  isBookingAgentAndisBookingAnUpgrade
} from '../helpers/userHelper'
import { pushHotelsToSessionStorage } from '../helpers/historyHelper'
import {
  getContentSectionHeight,
  getContentSectionHeightMobile,
} from '../helpers/contentRenderHelper'
import { getDepartureDateMoment } from '../helpers/hotelHelper'

const TEST_LOCALLY = false

const renderHotel = (hotel, index) => (
  <HotelItem key={'hotel' + index} hotel={hotel} active={hotel.id === index} />
)

const HotelsList = ({ setHotels }) => {
  const { state, dispatch } = useContext(SearchContext)
  const _setLoading = (payload) =>
    dispatch({ type: SEARCH.HOTELS.LOADING, payload: payload })

  let latitude = null
  let longitude = null
  let zoom = null
  if (state.currentHotels.length && state.currentHotels.length === 1) {
    latitude = state.currentHotels[0].latitude
    longitude = state.currentHotels[0].longitude
    zoom = 11
  } else {
    if(state.currentHotels.length) {
      latitude  = state.currentHotels[1].latitude
      longitude = state.currentHotels[1].longitude
    }

    zoom = 10
  }
  const [smallScreen, setSmallScreen] = useState(isMobileOnly)

  useEffect(() => {
    window.addEventListener('resize', _resizeActions)
    setSmallScreen(isMobileOnly || window.innerWidth < 765)
    // Once loaded, set loading to false
    _setLoading(false)
    return () => {
      window.removeEventListener('resize', _resizeActions)
    }
  }, [])

  useEffect(() => {
    if(!state.isLoading) {
      // Done loading page, push history
      _pushHistoryState()
    }
  }, [state.isLoading])

  const _pushHistoryState = () => {
    // Moment causes issues with history
    let newState = {...state}
    newState.arrival = state.arrival.format('YYYY-MM-DD')
    if (state.searchedArrival) {
      newState.searchedArrival = state.searchedArrival.format('YYYY-MM-DD')
    }
    newState.hotels = []
    newState.currentHotels = []
    newState.visibleHotels = []
    pushHotelsToSessionStorage(state)
    window.history.pushState({
      all: newState,
      resource: newState.searchBy
    }, 'Hotel Search Results', '/search')
  }

  const _resizeActions = () => {
    setSmallScreen(isMobileOnly || window.innerWidth < 765)
    _displayViews()
  }

  const _closeHandler = () => {
    dispatch({ type: SEARCH.BOOKING.SET, payload: null })
    dispatch({ type: SEARCH.POP_UP_OPEN.SET, payload: false })
  }

  const _setRoom = (payload) => dispatch({ type: SEARCH.ROOM.SET, payload })
  const _setRate = (payload) => dispatch({ type: SEARCH.RATE.SET, payload })
  const _setBooking = (payload) =>
    dispatch({ type: SEARCH.BOOKING.SET, payload })

  const _renderPagination = () => <HotelsPagination setHotels={setHotels} />

  const setRoomsTitle = (title, showSubtitle = false, showTotalText = false, showForNights = true) => {
    let verb = 'Total'
    if (isBookingAgentAndisBookingAnUpgrade(state)) {
      verb = 'Upgrade Total'
    }
    return (
      <Fragment>
        <h3 className="space-between">{title} {showTotalText && <small className="total-text">{verb}</small>}</h3>
        {
          showSubtitle &&
          <p className="thin-p space-between">
            <span>{moment(state.searchedArrival).format("MM/DD/YYYY")} to {
              moment(getDepartureDateMoment(state.searchedArrival, parseInt(state.searchedNights))).format("MM/DD/YYYY")
              }
            </span>
            { showForNights && <span>for {parseInt(state.searchedNights)} night(s)</span> }
          </p>
        }
      </Fragment>
    )
  }

  const _labelForBookRoomAndTour = () => {
    if (isTourOnlyUser(state.user)) {
      return 'Book Tour'
    } else if (isBookingAgentAndisBookingAnUpgrade(state)) {
      return "Book Room"
    } else {
      return "Book Room & Tour"
    }
  }

  const _renderRightColumn = () => {
    if (state.booking && state.booking.hotel === state.hotel) {
      return <HotelBooking setRoomsTitle={setRoomsTitle} />
    } else if (state.hotel && state.popUpOpen) {
      const actions = []
      if (shouldRenderBookNow(state)) {
        actions.push(
          <Button
            className="primary"
            clickHandler={() =>
            _setBooking({
              hotel: state.hotel,
              room: state.room,
              rate: state.rate,
              step: 'book_room',
              action: 'book_room',
            })
            }
            disabled={!state.room}
          >
              Book Room
          </Button>
        )
      }
      if (shouldRenderBookRoomsAndTour(state)) {
        actions.push(
          <Button
            className="secondary"
            clickHandler={() =>
            _setBooking({
              hotel: state.hotel,
              room: state.room,
              rate: state.rate,
              step: 'book_room_and_tour',
              action: 'book_room_and_tour',
            })
            }
            disabled={!state.room}
          >
            {_labelForBookRoomAndTour()}
          </Button>
        )
      }
      return (
        <HotelInfo
          hotel         = {state.hotel}
          closeHandler  = {_closeHandler}
          actions       = {actions}
        >
          <HotelRooms
            rooms         = {state.hotel.room_rates.rooms}
            amenities     = {state.hotel.facilities}
            setRoom       = {_setRoom}
            setRate       = {_setRate}
            room          = {state.room}
            selectedRate  = {state.rate}
            user          = {state.user}
            setRoomsTitle = {setRoomsTitle}
            hotel         = {state.hotel}
          />
        </HotelInfo>
      )
    }
  }

  const _renderAvailabilityNotices = () => {
    if (state.availabilityNotices.length == 0) {
      return
    }

    const availabilityLength = state.availabilityNotices.length
    return state.availabilityNotices.map((a, idx) => {
      return (
        <div className={`availability-notice-wrapper voc ${a.status} ${(availabilityLength == idx + 1) ? 'last' : ''}`}>
          <div className="availability-notice-container">
            <i className="fas fa-info info-icon" />
            <div dangerouslySetInnerHTML={{ __html: a.content }} />
          </div>
        </div>
      )
    })
  }

  const _renderHotels = () => state.visibleHotels.map(renderHotel)

  const _setPaginateClass = () => state.meta && state.meta.total_pages > 1 ? 'paginate' : ''

  const _displayMobileView = () => {
    return (
      <section className="content mobile">
        <SearchFilter />

        <div className="left column">
          {_renderAvailabilityNotices()}
          <ul className={`interactive rows hotels scrollable ${state.layout} ${_setPaginateClass()}`}>
            { _renderHotels() }
          </ul>
        </div>

        <div
          className={`right column ${
            state.popUpOpen || state.layout == 'map' ? 'open' : ''
          }${
            state.layout == 'map' ? ' map-open' : ''
          }`}
        >
          { _renderRightColumn() }
          {
            state.currentHotels.length &&
            <MapView
              center={{ lat: latitude, lng: longitude }}
              zoom={zoom}
              height={getContentSectionHeightMobile()}
            />
          }
        </div>
      </section>
    )
  }

  const _displayDesktopView = () => {
    return (
      <section className="content">
        <div className="left column">
          <SearchFilter />

          <ul className={`interactive rows hotels scrollable ${state.layout} ${_setPaginateClass()}${
            state.visibleHotels.length === 1 ? 'single' : ''
          }`}>
            {_renderAvailabilityNotices()}
            { _renderHotels() }
          </ul>
          { TEST_LOCALLY ? _renderPagination() : state.meta && state.meta.total_pages > 1 && _renderPagination() }
        </div>

        <div className="right column">
          {_renderRightColumn()}
          {
            state.currentHotels.length &&
            <MapView
              center={{ lat: latitude, lng: longitude }}
              zoom={zoom}
              height={getContentSectionHeight()}
            />
          }
        </div>
      </section>
    )
  }

  const _displayViews = () => {
    return (
      <React.Fragment>
        {smallScreen && _displayMobileView()}
        {!smallScreen && _displayDesktopView()}
      </React.Fragment>
    )
  }

  return <React.Fragment>{_displayViews()}</React.Fragment>
}

HotelsList.propTypes = {
  setHotels: PropTypes.func.isRequired
}

export default HotelsList
